import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import SimpleRadarChart from './SimpleRadarChart';
import SimpleDepreciationChart from './SimpleDepreciationChart';

const AnalysisContainer = styled(motion.div)`
  margin-top: 40px;
  padding: 40px;
  background: linear-gradient(145deg, #1E1E1E 0%, #2A2A2A 100%);
  border-radius: 16px;
  border: 1px solid #333;
  
  @media (max-width: 768px) {
    padding: 24px;
    margin-top: 32px;
  }
`;

const VehicleHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
  padding-bottom: 32px;
  border-bottom: 1px solid #333;
`;

const VehicleTitle = styled.h2`
  font-size: 32px;
  color: #2196F3;
  margin-bottom: 8px;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const VehicleSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 18px;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const VehicleSpecs = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 24px;
`;

const SpecItem = styled.div`
  background-color: #121212;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #444;
  text-align: center;
  
  h4 {
    color: #2196F3;
    font-size: 14px;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  
  p {
    color: #FFFFFF;
    font-size: 16px;
    font-weight: 600;
    margin: 0;
  }
`;

const ScoresSection = styled.div`
  margin: 40px 0;
`;

const SectionTitle = styled.h3`
  font-size: 24px;
  color: #FFFFFF;
  margin-bottom: 24px;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const ScoresGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
`;

const ScoreCard = styled.div`
  background-color: #121212;
  padding: 24px;
  border-radius: 12px;
  border: 1px solid #444;
  text-align: center;
  
  h4 {
    color: #2196F3;
    font-size: 16px;
    margin-bottom: 16px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
`;


const ScoreValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.score < 4 ? '#F44336' : 
    props.score < 7 ? '#FF9800' : '#4CAF50'};
  margin-bottom: 8px;
`;

const ScoreDescription = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
`;

const DepreciationSection = styled.div`
  margin: 40px 0;
  background-color: #121212;
  padding: 24px;
  border-radius: 12px;
  border: 1px solid #444;
`;


const DepreciationTable = styled.div`
  margin-top: 24px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
`;

const DepreciationItem = styled.div`
  background-color: #1A1A1A;
  padding: 12px;
  border-radius: 8px;
  text-align: center;
  
  .year {
    color: #2196F3;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 4px;
  }
  
  .value {
    color: #FFFFFF;
    font-size: 16px;
    font-weight: 700;
  }
`;

const IssuesSection = styled.div`
  margin: 40px 0;
`;

const IssuesList = styled.div`
  background-color: #121212;
  padding: 24px;
  border-radius: 12px;
  border: 1px solid #444;
`;

const IssueItem = styled.div`
  background-color: #1A1A1A;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  border-left: 4px solid ${props => 
    props.severity === 'High' ? '#F44336' :
    props.severity === 'Medium' ? '#FF9800' : '#4CAF50'
  };
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const IssueHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const IssueTitle = styled.h4`
  color: #FFFFFF;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  flex: 1;
`;

const SeverityBadge = styled.span`
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${props => 
    props.severity === 'High' ? 'rgba(244, 67, 54, 0.2)' :
    props.severity === 'Medium' ? 'rgba(255, 152, 0, 0.2)' : 'rgba(76, 175, 80, 0.2)'
  };
  color: ${props => 
    props.severity === 'High' ? '#F44336' :
    props.severity === 'Medium' ? '#FF9800' : '#4CAF50'
  };
`;

const IssueDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  line-height: 1.6;
  margin: 0 0 12px 0;
`;

const IssueCost = styled.div`
  color: #2196F3;
  font-size: 13px;
  font-weight: 600;
  
  span {
    color: rgba(255, 255, 255, 0.6);
    font-weight: normal;
  }
`;

const UpgradePrompt = styled.div`
  background: linear-gradient(135deg, #2196F3 0%, #1976D2 100%);
  border-radius: 12px;
  padding: 24px;
  margin-top: 16px;
  text-align: center;
  border: 1px solid #1976D2;
`;

const UpgradeTitle = styled.h4`
  color: #FFFFFF;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 12px 0;
`;

const UpgradeDescription = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 16px 0;
`;

const UpgradeLink = styled.a`
  display: inline-block;
  background-color: #FFFFFF;
  color: #1976D2;
  padding: 12px 24px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #F5F5F5;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

const ChecklistSection = styled.div`
  margin: 40px 0;
`;

const ChecklistGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
`;

const ChecklistCategory = styled.div`
  background-color: #121212;
  padding: 24px;
  border-radius: 12px;
  border: 1px solid #444;
`;

const CategoryTitle = styled.h4`
  color: #2196F3;
  font-size: 16px;
  margin-bottom: 16px;
  font-weight: 600;
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .checkbox {
    width: 16px;
    height: 16px;
    border: 2px solid #2196F3;
    border-radius: 3px;
    margin-right: 12px;
    margin-top: 2px;
    background-color: transparent;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      background-color: rgba(33, 150, 243, 0.1);
    }
  }
  
  .text {
    color: rgba(255, 255, 255, 0.9);
    font-size: 14px;
    line-height: 1.5;
    flex: 1;
  }
`;

const RecallsSection = styled.div`
  margin: 40px 0;
  background-color: ${props => props.hasRecalls ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)'};
  border: 1px solid ${props => props.hasRecalls ? '#F44336' : '#4CAF50'};
  padding: 24px;
  border-radius: 12px;
`;

const RecallStatus = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  
  .icon {
    font-size: 24px;
    margin-right: 12px;
    color: ${props => props.hasRecalls ? '#F44336' : '#4CAF50'};
  }
  
  .text {
    color: #FFFFFF;
    font-size: 18px;
    font-weight: 600;
  }
`;

const CarAnalysis = ({ vehicleData, analysisData }) => {
  if (!vehicleData || !analysisData) return null;

  const { make, model, year, trim, engineSize, transmission, fuelType, driveType } = vehicleData;
  const { 
    scores, 
    fuelEconomyMPG, 
    depreciationData = [], 
    potentialIssues = [], 
    buyingChecklist = [], 
    recalls = { hasRecalls: false, count: 0, description: 'No recall information available' }, 
    marketInsights, 
    buyingRecommendations = [] 
  } = analysisData;


  return (
    <AnalysisContainer
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <VehicleHeader>
        <VehicleTitle>{year} {make} {model}</VehicleTitle>
        <VehicleSubtitle>{trim && `${trim} • `}Comprehensive Analysis Report</VehicleSubtitle>
        
        <VehicleSpecs>
          <SpecItem>
            <h4>Engine</h4>
            <p>{engineSize}L</p>
          </SpecItem>
          <SpecItem>
            <h4>Transmission</h4>
            <p>{transmission}</p>
          </SpecItem>
          <SpecItem>
            <h4>Fuel Type</h4>
            <p>{fuelType}</p>
          </SpecItem>
          <SpecItem>
            <h4>Drive Type</h4>
            <p>{driveType}</p>
          </SpecItem>
          <SpecItem>
            <h4>Fuel Economy</h4>
            <p>{fuelEconomyMPG} MPG</p>
          </SpecItem>
        </VehicleSpecs>
      </VehicleHeader>

      <ScoresSection>
        <SectionTitle>5-Dimensional Vehicle Analysis</SectionTitle>
        <SimpleRadarChart scores={scores} />
        
        <ScoresGrid>
          <ScoreCard>
            <h4>Reliability</h4>
            <ScoreValue score={scores.reliability}>
              {scores.reliability.toFixed(1)}/10
            </ScoreValue>
            <ScoreDescription>
              Overall dependability and expected longevity
            </ScoreDescription>
          </ScoreCard>

          <ScoreCard>
            <h4>Performance</h4>
            <ScoreValue score={scores.performance}>
              {scores.performance.toFixed(1)}/10
            </ScoreValue>
            <ScoreDescription>
              Power, acceleration, and driving dynamics
            </ScoreDescription>
          </ScoreCard>

          <ScoreCard>
            <h4>Fuel Economy</h4>
            <ScoreValue score={scores.fuelEconomy}>
              {scores.fuelEconomy.toFixed(1)}/10
            </ScoreValue>
            <ScoreDescription>
              Gas mileage: {fuelEconomyMPG} MPG
            </ScoreDescription>
          </ScoreCard>

          <ScoreCard>
            <h4>Total Cost of Ownership</h4>
            <ScoreValue score={scores.totalCostOfOwnership}>
              {scores.totalCostOfOwnership.toFixed(1)}/10
            </ScoreValue>
            <ScoreDescription>
              Insurance, maintenance, and depreciation costs
            </ScoreDescription>
          </ScoreCard>

          <ScoreCard>
            <h4>Resale Value</h4>
            <ScoreValue score={scores.resaleValue}>
              {scores.resaleValue.toFixed(1)}/10
            </ScoreValue>
            <ScoreDescription>
              Expected value retention over time
            </ScoreDescription>
          </ScoreCard>
        </ScoresGrid>
      </ScoresSection>

      {depreciationData && depreciationData.length > 0 && (
        <DepreciationSection>
          <SectionTitle>Price Depreciation Analysis</SectionTitle>
          <SimpleDepreciationChart depreciationData={depreciationData} />
          
          <DepreciationTable>
            {depreciationData.slice(-6).map((item, index) => (
              <DepreciationItem key={index}>
                <div className="year">
                  {item.year} {item.isCurrent && '📍'} {item.isFuture && '📈'}
                </div>
                <div className="value">${item.value.toLocaleString()}</div>
              </DepreciationItem>
            ))}
          </DepreciationTable>
        </DepreciationSection>
      )}

      {marketInsights && (
        <ScoresSection>
          <SectionTitle>Market Insights</SectionTitle>
          <ScoresGrid>
            <ScoreCard>
              <h4>Market Demand</h4>
              <ScoreValue score={marketInsights.demandLevel === 'High' ? 9 : marketInsights.demandLevel === 'Medium' ? 6 : 3}>
                {marketInsights.demandLevel}
              </ScoreValue>
              <ScoreDescription>
                Current market demand for this vehicle
              </ScoreDescription>
            </ScoreCard>

            <ScoreCard>
              <h4>Price Stability</h4>
              <ScoreValue score={marketInsights.priceStability === 'Stable' ? 8 : marketInsights.priceStability === 'Declining' ? 4 : 6}>
                {marketInsights.priceStability}
              </ScoreValue>
              <ScoreDescription>
                Expected price trend in the market
              </ScoreDescription>
            </ScoreCard>

            <ScoreCard>
              <h4>Best Buying Time</h4>
              <ScoreValue score={7}>
                {marketInsights.bestBuyingTime}
              </ScoreValue>
              <ScoreDescription>
                Optimal timing for purchase decision
              </ScoreDescription>
            </ScoreCard>
          </ScoresGrid>
        </ScoresSection>
      )}

      {buyingRecommendations && buyingRecommendations.length > 0 && (
        <IssuesSection>
          <SectionTitle>Buying Recommendations</SectionTitle>
          <IssuesList>
            {buyingRecommendations.slice(0, 2).map((recommendation, index) => (
              <IssueItem key={index} severity="Medium">
                <IssueHeader>
                  <IssueTitle>💡 {recommendation}</IssueTitle>
                </IssueHeader>
              </IssueItem>
            ))}
            {buyingRecommendations.length > 2 && (
              <UpgradePrompt>
                <UpgradeTitle>
                  +{buyingRecommendations.length - 2} more detailed recommendations
                </UpgradeTitle>
                <UpgradeDescription>
                  Get comprehensive buying strategies, negotiation tips, and detailed market analysis in the full version.
                </UpgradeDescription>
                <UpgradeLink href="https://app.carconsul.com" target="_blank" rel="noopener noreferrer">
                  Get Full Report
                </UpgradeLink>
              </UpgradePrompt>
            )}
          </IssuesList>
        </IssuesSection>
      )}

      <RecallsSection hasRecalls={recalls.hasRecalls}>
        <SectionTitle>Recall Information</SectionTitle>
        <RecallStatus hasRecalls={recalls.hasRecalls}>
          <span className="icon">
            {recalls.hasRecalls ? '⚠️' : '✅'}
          </span>
          <span className="text">
            {recalls.hasRecalls 
              ? `Potential recalls found (${recalls.count})` 
              : 'No known recalls for this model'
            }
          </span>
        </RecallStatus>
        <p style={{ color: 'rgba(255, 255, 255, 0.7)', margin: 0 }}>
          {recalls.description}
        </p>
      </RecallsSection>

      {potentialIssues && potentialIssues.length > 0 && (
        <IssuesSection>
          <SectionTitle>Vehicle-Specific Issues to Check</SectionTitle>
          <IssuesList>
            {(() => {
              // Filter issues by severity
              const lowIssues = potentialIssues.filter(issue => 
                typeof issue === 'string' ? false : issue.severity === 'Low'
              );
              const mediumIssues = potentialIssues.filter(issue => 
                typeof issue === 'string' ? true : issue.severity === 'Medium'
              );
              const highIssues = potentialIssues.filter(issue => 
                typeof issue === 'string' ? false : issue.severity === 'High'
              );
              
              // Show first 2 low issues, or first 2 issues if no low ones
              const issuesToShow = lowIssues.length >= 2 ? lowIssues.slice(0, 2) : potentialIssues.slice(0, 2);
              const remainingMedium = mediumIssues.length - (lowIssues.length < 2 ? Math.max(0, 2 - lowIssues.length) : 0);
              const remainingHigh = highIssues.length;
              
              return (
                <>
                  {issuesToShow.map((issue, index) => {
                    // Handle both string and object formats
                    if (typeof issue === 'string') {
                      return (
                        <IssueItem key={index} severity="Low">
                          <IssueHeader>
                            <IssueTitle>⚠️ {issue}</IssueTitle>
                            <SeverityBadge severity="Low">Low</SeverityBadge>
                          </IssueHeader>
                        </IssueItem>
                      );
                    }
                    
                    return (
                      <IssueItem key={index} severity={issue.severity}>
                        <IssueHeader>
                          <IssueTitle>{issue.issue}</IssueTitle>
                          <SeverityBadge severity={issue.severity}>
                            {issue.severity}
                          </SeverityBadge>
                        </IssueHeader>
                        <IssueDescription>
                          {issue.explanation}
                        </IssueDescription>
                        {issue.cost && (
                          <IssueCost>
                            <span>Estimated repair cost: </span>{issue.cost}
                          </IssueCost>
                        )}
                      </IssueItem>
                    );
                  })}
                  
                  {(remainingMedium > 0 || remainingHigh > 0) && (
                    <UpgradePrompt>
                      <UpgradeTitle>
                        {remainingHigh > 0 && `🔴 ${remainingHigh} High Severity Issues`}
                        {remainingHigh > 0 && remainingMedium > 0 && ' • '}
                        {remainingMedium > 0 && `🟡 ${remainingMedium} Medium Severity Issues`}
                      </UpgradeTitle>
                      <UpgradeDescription>
                        Critical technical problems that could cost thousands in repairs. Get detailed explanations, 
                        repair timelines, and specific component information in the full analysis.
                      </UpgradeDescription>
                      <UpgradeLink href="https://app.carconsul.com" target="_blank" rel="noopener noreferrer">
                        See All Issues
                      </UpgradeLink>
                    </UpgradePrompt>
                  )}
                </>
              );
            })()}
          </IssuesList>
        </IssuesSection>
      )}

      {buyingChecklist && buyingChecklist.length > 0 && (
        <ChecklistSection>
          <SectionTitle>Pre-Purchase Inspection Checklist</SectionTitle>
          <ChecklistGrid>
            {buyingChecklist.map((category, categoryIndex) => (
              <ChecklistCategory key={categoryIndex}>
                <CategoryTitle>{category.category}</CategoryTitle>
                {category.items && category.items.map((item, itemIndex) => (
                  <ChecklistItem key={itemIndex}>
                    <div className="checkbox"></div>
                    <span className="text">{item}</span>
                  </ChecklistItem>
                ))}
              </ChecklistCategory>
            ))}
          </ChecklistGrid>
        </ChecklistSection>
      )}

      <UpgradePrompt style={{ marginTop: '48px', padding: '32px' }}>
        <UpgradeTitle style={{ fontSize: '24px', marginBottom: '16px' }}>
          🚗 Get Your Complete Vehicle Analysis
        </UpgradeTitle>
        <UpgradeDescription style={{ fontSize: '16px', marginBottom: '24px' }}>
          This is a preview of our comprehensive vehicle analysis. Get the complete report with all technical issues, 
          detailed repair costs, market insights, and professional buying recommendations.
        </UpgradeDescription>
        <UpgradeLink 
          href="https://app.carconsul.com" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ fontSize: '16px', padding: '16px 32px' }}
        >
          Get Full Report
        </UpgradeLink>
      </UpgradePrompt>
    </AnalysisContainer>
  );
};

export default CarAnalysis;